/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from "prop-types";
import classnames from 'classnames';
import { tabbable } from 'tabbable';
import { EuiFocusTrap } from '../focus_trap';
import { EuiPopover } from './popover';
import { EuiResizeObserver } from '../observer/resize_observer';
import { cascadingMenuKeys, useCombinedRefs, useEuiTheme } from '../../services';
import { euiFormVariables } from '../form/form.styles';
import { css } from '@emotion/react';
import { logicalCSS } from '../../global_styling';
export var EuiInputPopover = ({
  children,
  className,
  disableFocusTrap = false,
  focusTrapProps,
  input,
  fullWidth = false,
  panelMinWidth = 0,
  onPanelResize,
  inputRef: _inputRef,
  panelRef: _panelRef,
  ...props
}) => {
  const euiThemeContext = useEuiTheme();
  const [inputEl, setInputEl] = useState(null);
  const [inputElWidth, setInputElWidth] = useState();
  const [panelEl, setPanelEl] = useState(null);
  const popoverClassRef = useRef(null);
  const inputRef = useCombinedRefs([setInputEl, _inputRef]);
  const panelRef = useCombinedRefs([setPanelEl, _panelRef]);
  const setPanelWidth = useCallback(width => {
    if (panelEl && (!!inputElWidth || !!width)) {
      const newWidth = !!width ? width : inputElWidth;
      const widthToSet = newWidth && newWidth > panelMinWidth ? newWidth : panelMinWidth;
      panelEl.style.width = `${widthToSet}px`;
      onPanelResize?.(widthToSet);
    }
  }, [panelEl, inputElWidth, onPanelResize, panelMinWidth]);
  const onResize = useCallback(() => {
    if (inputEl) {
      const width = inputEl.getBoundingClientRect().width;
      setInputElWidth(width);
      setPanelWidth(width);
      popoverClassRef.current?.positionPopoverFluid();
    }
  }, [inputEl, setPanelWidth]);
  useEffect(() => {
    onResize();
  }, [onResize]);
  useEffect(() => {
    setPanelWidth();
  }, [setPanelWidth]);
  const onKeyDown = event => {
    if (panelEl && event.key === cascadingMenuKeys.TAB) {
      const tabbableItems = tabbable(panelEl).filter(el => {
        return Array.from(el.attributes).map(el => el.name).indexOf('data-focus-guard') < 0;
      });
      if (disableFocusTrap || tabbableItems.length && tabbableItems[tabbableItems.length - 1] === document.activeElement) {
        props.closePopover();
      }
    }
  };
  const classes = classnames('euiInputPopover', className);
  const form = euiFormVariables(euiThemeContext);
  return <EuiPopover css={/*#__PURE__*/css(fullWidth ? undefined : logicalCSS('max-width', form.maxWidth), ";label:EuiInputPopover;")} ownFocus={false} button={<EuiResizeObserver onResize={onResize}>
          {resizeRef => <div ref={resizeRef}>{input}</div>}
        </EuiResizeObserver>} buttonRef={inputRef} panelRef={panelRef} className={classes} ref={popoverClassRef} {...props}>
      <EuiFocusTrap clickOutsideDisables={true} disabled={disableFocusTrap} {...focusTrapProps}>
        <div onKeyDown={onKeyDown}>{children}</div>
      </EuiFocusTrap>
    </EuiPopover>;
};
EuiInputPopover.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
     * Alignment of the popover relative to the input
     */
  anchorPosition: PropTypes.oneOf(["downLeft", "downRight", "downCenter"]),
  disableFocusTrap: PropTypes.bool,
  fullWidth: PropTypes.bool,
  input: PropTypes.any.isRequired,
  inputRef: PropTypes.any,
  onPanelResize: PropTypes.func,
  /**
     * By default, **EuiInputPopovers** inherit the same width as the passed input element.
     * However, if the input width is too small, you can pass a minimum panel width
     * (that should be based on the popover content).
     */
  panelMinWidth: PropTypes.number
};
EuiInputPopover.defaultProps = {
  anchorPosition: 'downLeft',
  attachToAnchor: true,
  display: 'block',
  panelPaddingSize: 's'
};
try {
  EuiInputPopover.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/popover/input_popover.tsx',
    description: '',
    displayName: 'EuiInputPopover',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + 'Provide a name to the popover panel\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      anchorPosition: {
        defaultValue: {
          value: 'downLeft'
        },
        description: 'Alignment of the popover relative to the input',
        name: 'anchorPosition',
        parent: {
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"downCenter" | "downLeft" | "downRight"',
          value: [{
            value: '"downCenter"'
          }, {
            value: '"downLeft"'
          }, {
            value: '"downRight"'
          }]
        }
      },
      disableFocusTrap: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'disableFocusTrap',
        parent: {
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'fullWidth',
        parent: {
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      input: {
        defaultValue: null,
        description: '',
        name: 'input',
        parent: {
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      inputRef: {
        defaultValue: null,
        description: '',
        name: 'inputRef',
        parent: {
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLDivElement) => void'
        }
      },
      onPanelResize: {
        defaultValue: null,
        description: '',
        name: 'onPanelResize',
        parent: {
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: '(width?: number) => void'
        }
      },
      panelMinWidth: {
        defaultValue: {
          value: '0'
        },
        description: 'By default, **EuiInputPopovers** inherit the same width as the passed input element.\n' + 'However, if the input width is too small, you can pass a minimum panel width\n' + '(that should be based on the popover content).',
        name: 'panelMinWidth',
        parent: {
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/input_popover.tsx',
          name: '_EuiInputPopoverProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      container: {
        defaultValue: null,
        description: "Restrict the popover's position within this element",
        name: 'container',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'HTMLElement'
        }
      },
      display: {
        defaultValue: {
          value: 'block'
        },
        description: 'CSS display type for both the popover and anchor',
        name: 'display',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Display'
        }
      },
      offset: {
        defaultValue: null,
        description: 'Distance away from the anchor that the popover will render',
        name: 'offset',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      panelRef: {
        defaultValue: null,
        description: '',
        name: 'panelRef',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLElement) => void'
        }
      },
      ownFocus: {
        defaultValue: null,
        description: 'Traps tab focus within the popover contents',
        name: 'ownFocus',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      focusTrapProps: {
        defaultValue: null,
        description: 'Object of props passed to EuiFocusTrap',
        name: 'focusTrapProps',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Partial<EuiFocusTrapProps>'
        }
      },
      isOpen: {
        defaultValue: null,
        description: 'Visibility state of the popover',
        name: 'isOpen',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      anchorClassName: {
        defaultValue: null,
        description: 'Class name passed to the direct parent of the button',
        name: 'anchorClassName',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      attachToAnchor: {
        defaultValue: {
          value: 'true'
        },
        description: 'Style and position alteration for arrow-less attachment.\n' + 'Intended for use with inputs as anchors, e.g. EuiInputPopover',
        name: 'attachToAnchor',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      closePopover: {
        defaultValue: null,
        description: 'Callback to handle hiding of the popover',
        name: 'closePopover',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: true,
        type: {
          name: 'NoArgCallback<void>'
        }
      },
      hasArrow: {
        defaultValue: null,
        description: 'Show arrow indicating to originating button',
        name: 'hasArrow',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      initialFocus: {
        defaultValue: null,
        description: 'Specifies what element should initially have focus; Can be a DOM\n' + 'node, or a selector string (which will be passed to\n' + 'document.querySelector() to find the DOM node), or a function that\n' + 'returns a DOM node.\n' + '\n' + 'If not passed, initial focus defaults to the popover panel.',
        name: 'initialFocus',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'ElementTarget'
        }
      },
      insert: {
        defaultValue: null,
        description: 'Passed directly to EuiPortal for DOM positioning. Both properties are\n' + 'required if prop is specified',
        name: 'insert',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: '{ sibling: HTMLElement; position: "after" | "before"; }'
        }
      },
      panelClassName: {
        defaultValue: null,
        description: 'Custom class added to the EuiPanel containing the popover contents',
        name: 'panelClassName',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      panelPaddingSize: {
        defaultValue: {
          value: 's'
        },
        description: 'EuiPanel padding on all sides',
        name: 'panelPaddingSize',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "none" | "m" | "xs" | "l" | "xl"',
          value: [{
            value: '"s"'
          }, {
            value: '"none"'
          }, {
            value: '"m"'
          }, {
            value: '"xs"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      panelStyle: {
        defaultValue: null,
        description: 'Standard DOM `style` attribute. Passed to the EuiPanel',
        name: 'panelStyle',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      panelProps: {
        defaultValue: null,
        description: 'Object of props passed to EuiPanel. See #EuiPopoverPanelProps',
        name: 'panelProps',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Omit<_EuiPanelDivlike, "style" | "hasBorder" | "hasShadow">'
        }
      },
      popoverScreenReaderText: {
        defaultValue: null,
        description: 'Optional screen reader instructions to announce upon popover open,\n' + "in addition to EUI's default popover instructions for Escape on close.\n" + 'Useful for popovers that may have additional keyboard capabilities such as\n' + 'arrow navigation.',
        name: 'popoverScreenReaderText',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      popoverRef: {
        defaultValue: null,
        description: '',
        name: 'popoverRef',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLDivElement>'
        }
      },
      repositionOnScroll: {
        defaultValue: null,
        description: "When `true`, the popover's position is re-calculated when the user\n" + 'scrolls, this supports having fixed-position popover anchors. When nesting\n' + 'an `EuiPopover` in a scrollable container, `repositionOnScroll` should be `true`',
        name: 'repositionOnScroll',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      hasDragDrop: {
        defaultValue: null,
        description: 'Must be set to true if using `EuiDragDropContext` within a popover,\n' + 'otherwise your nested drag & drop will have incorrect positioning',
        name: 'hasDragDrop',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      zIndex: {
        defaultValue: null,
        description: 'By default, popover content inherits the z-index of the anchor\n' + 'component; pass `zIndex` to override',
        name: 'zIndex',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      buffer: {
        defaultValue: null,
        description: 'Minimum distance between the popover and the bounding container;\n' + 'Pass an array of 4 values to adjust each side differently: `[top, right, bottom, left]`\n' + 'Default is 16',
        name: 'buffer',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'number | [number, number, number, number]'
        }
      },
      arrowChildren: {
        defaultValue: null,
        description: 'Element to pass as the child element of the arrow;\n' + 'Use case is typically limited to an accompanying `EuiBeacon`',
        name: 'arrowChildren',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      onPositionChange: {
        defaultValue: null,
        description: 'Function callback for when the popover positon changes',
        name: 'onPositionChange',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: '(position: EuiPopoverPosition) => void'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', '_EuiInputPopoverProps', 'EuiPopoverProps']
  };
} catch (e) {}